interface RGB {
  r: number;
  g: number;
  b: number;
}

interface ColorCount extends RGB {
  count: number;
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;
  const sum = max + min;

  const l = sum / 2;

  if (diff === 0) {
    return [0, 0, l]; // achromatic
  }

  const s = l > 0.5 ? diff / (2 - sum) : diff / sum;

  let h = 0;
  switch (max) {
    case r:
      h = ((g - b) / diff + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / diff + 2) / 6;
      break;
    case b:
      h = ((r - g) / diff + 4) / 6;
      break;
  }

  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const gray = Math.round(l * 255);
    return [gray, gray, gray]; // achromatic
  }

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  const r = hue2rgb(p, q, h + 1 / 3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1 / 3);

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function isNearWhite(r: number, g: number, b: number): boolean {
  // Check if color is very close to white (high lightness and low saturation)
  const [, s, l] = rgbToHsl(r, g, b);
  return l > 0.9 && s < 0.1;
}

function toneDownColor(r: number, g: number, b: number): [number, number, number] {
  const [h, s, l] = rgbToHsl(r, g, b);

  // Reduce saturation significantly (make it more muted)
  const newSaturation = s * 0.3; // Reduce saturation to 30% of original

  // Adjust lightness to be more background-appropriate
  // If it's too dark, lighten it; if it's too light, slightly darken it
  let newLightness = l;
  if (l < 0.3) {
    // Too dark - lighten it significantly
    newLightness = 0.85;
  } else if (l > 0.8) {
    // Too light - darken it slightly
    newLightness = 0.75;
  } else {
    // In middle range - make it lighter and more subtle
    newLightness = Math.min(0.85, l + 0.3);
  }

  const [tonedR, tonedG, tonedB] = hslToRgb(h, newSaturation, newLightness);

  // If the result is near white, make it noticeably darker with some color
  if (isNearWhite(tonedR, tonedG, tonedB)) {
    // Use a warm neutral gray with slight hue variation
    const fallbackHue = h || 0.08; // Use original hue or warm beige if no hue
    const fallbackSaturation = Math.max(0.15, newSaturation); // Ensure some color
    const fallbackLightness = 0.7; // Noticeably darker than white

    return hslToRgb(fallbackHue, fallbackSaturation, fallbackLightness);
  }

  return [tonedR, tonedG, tonedB];
}

function getColorDistance(color1: RGB, color2: RGB): number {
  const rDiff = color1.r - color2.r;
  const gDiff = color1.g - color2.g;
  const bDiff = color1.b - color2.b;
  return Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff);
}

function shouldIgnoreColor(r: number, g: number, b: number): boolean {
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // Ignore very light colors (likely backgrounds)
  if (brightness > 240) return true;

  // Ignore very dark colors (likely shadows/text)
  if (brightness < 30) return true;

  // Ignore grayscale colors (low saturation)
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const saturation = max === 0 ? 0 : (max - min) / max;
  if (saturation < 0.2) return true;

  return false;
}

function clusterColors(colors: ColorCount[], threshold = 50): ColorCount[] {
  const clusters: ColorCount[] = [];

  for (const color of colors) {
    let foundCluster = false;

    for (const cluster of clusters) {
      if (getColorDistance(color, cluster) < threshold) {
        cluster.r = Math.round(
          (cluster.r * cluster.count + color.r * color.count) / (cluster.count + color.count),
        );
        cluster.g = Math.round(
          (cluster.g * cluster.count + color.g * color.count) / (cluster.count + color.count),
        );
        cluster.b = Math.round(
          (cluster.b * cluster.count + color.b * color.count) / (cluster.count + color.count),
        );
        cluster.count += color.count;
        foundCluster = true;
        break;
      }
    }

    if (!foundCluster) {
      clusters.push({ ...color });
    }
  }

  return clusters.sort((a, b) => b.count - a.count);
}

export function extractDominantColor(imageUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve('#f0f0f0'); // Light gray instead of white
          return;
        }

        // Scale down for performance
        const maxSize = 100;
        const scale = Math.min(maxSize / img.width, maxSize / img.height);
        canvas.width = Math.floor(img.width * scale);
        canvas.height = Math.floor(img.height * scale);

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        const colorMap = new Map<string, ColorCount>();

        // Sample pixels (skip some for performance)
        const step = 4;
        for (let i = 0; i < data.length; i += step * 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];

          // Skip transparent pixels
          if (a < 128) continue;

          // Skip colors we want to ignore
          if (shouldIgnoreColor(r, g, b)) continue;

          // Quantize colors to reduce noise
          const qr = Math.round(r / 10) * 10;
          const qg = Math.round(g / 10) * 10;
          const qb = Math.round(b / 10) * 10;

          const key = `${qr},${qg},${qb}`;

          if (colorMap.has(key)) {
            colorMap.get(key)!.count++;
          } else {
            colorMap.set(key, { r: qr, g: qg, b: qb, count: 1 });
          }
        }

        const colors = Array.from(colorMap.values());

        if (colors.length === 0) {
          resolve('#f0f0f0'); // Light gray instead of white
          return;
        }

        // Cluster similar colors
        const clusteredColors = clusterColors(colors);

        // Return the most common color, but toned down
        const dominantColor = clusteredColors[0];
        const [tonedR, tonedG, tonedB] = toneDownColor(
          dominantColor.r,
          dominantColor.g,
          dominantColor.b,
        );
        resolve(rgbToHex(tonedR, tonedG, tonedB));
      } catch (_error) {
        resolve('#f0f0f0'); // Light gray instead of white
      }
    };

    img.onerror = () => {
      resolve('#f0f0f0'); // Light gray instead of white
    };

    img.src = imageUrl;
  });
}
